import React, { useEffect, useState } from "react";
import { fetchFromStrapi } from "../api/strapi";
import IpSkeletonLoader from "./IpSkeletonLoader";
import { SkeletonBlock, EmptyState } from "./SkeletonUI";

function IpCard({ item, onClick }) {
  const [hovered, setHovered] = useState(false);
  const logoUrl = item.attributes?.logo?.data?.[0]?.attributes?.url;

  return (
    <div
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        background: "#fff",
        border: "1px solid #e5e7eb",
        borderRadius: 14,
        padding: "32px 24px 28px",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        textAlign: "center",
        cursor: "pointer",
        transition: "box-shadow 0.22s ease, transform 0.22s ease",
        boxShadow: hovered ? "0 12px 32px rgba(0,0,0,0.10)" : "0 2px 8px rgba(0,0,0,0.05)",
        transform: hovered ? "translateY(-4px)" : "translateY(0)",
      }}
    >
      {logoUrl && (
        <div style={{ height: 72, display: "flex", alignItems: "center", marginBottom: 16 }}>
          <img
            src={logoUrl}
            alt={item.attributes?.title}
            loading="lazy"
            decoding="async"
            style={{ maxHeight: 72, maxWidth: 160, objectFit: "contain" }}
          />
        </div>
      )}
      <h3 style={{
        fontWeight: 700,
        fontSize: "1rem",
        color: "#0a1a3a",
        marginBottom: 12,
        lineHeight: 1.35,
      }}>
        {item.attributes?.title}
      </h3>
      <span style={{ color: "#009EDC", fontSize: "0.8rem", fontWeight: 600 }}>
        View details 
      </span>
    </div>
  );
}

const IpCarousel = () => {
  const [ips, setIps] = useState([]);
  const [loading, setLoading] = useState(true);

  const [modalOpen, setModalOpen] = useState(false);
  const [modalLoading, setModalLoading] = useState(false);
  const [ipDetails, setIpDetails] = useState(null);

  useEffect(() => {
    async function fetchData() {
      try {
        const res = await fetchFromStrapi("/ecl-Ips");
        setIps(res?.data?.data || res?.data || []);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    }
    fetchData();
  }, []);

  const openModal = async (ipId) => {
    setModalOpen(true);
    setModalLoading(true);
    setIpDetails(null);
    try {
      const res = await fetchFromStrapi(`/ecl-Ips/${ipId}`);
      setIpDetails(res?.data?.data || res?.data);
    } catch (err) {
      console.error("Failed to load IP details", err);
    } finally {
      setModalLoading(false);
    }
  };

  const closeModal = () => {
    setModalOpen(false);
    setIpDetails(null);
  };

  if (loading) return <IpSkeletonLoader />;

  return (
    <section className="container" style={{ padding: "72px 0 80px" }}>
      <p className="text-uppercase fw-semibold mb-2"
        style={{ fontSize: "0.75rem", letterSpacing: "0.1em", color: "#009EDC" }}>
        Built in-house
      </p>
      <h2 className="fw-bold mb-5"
        style={{ fontSize: "clamp(1.75rem, 2.5vw, 2.25rem)", color: "#0a1a3a" }}>
        Our Products
      </h2>

      {ips.length === 0 ? (
        <EmptyState
          title="No products yet"
          description="Our in-house products will be listed here soon."
        />
      ) : (
        <div style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))",
          gap: 20,
        }}>
          {ips.map(item => (
            <IpCard
              key={item.id}
              item={item}
              onClick={() => openModal(item.id)}
            />
          ))}
        </div>
      )}

      {/* Detail modal */}
      {modalOpen && (
        <div
          onClick={closeModal}
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(10,26,58,0.6)",
            backdropFilter: "blur(4px)",
            zIndex: 1050,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 20,
          }}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{
              background: "#fff",
              borderRadius: 16,
              maxWidth: 680,
              width: "100%",
              maxHeight: "90vh",
              overflowY: "auto",
              boxShadow: "0 24px 64px rgba(0,0,0,0.22)",
              overflow: "hidden",
            }}
          >
            {/* Accent bar */}
            <div style={{ height: 4, background: "linear-gradient(90deg, #009EDC, #00ddff)", flexShrink: 0 }} />

            <div style={{ padding: "32px 36px", overflowY: "auto", maxHeight: "calc(90vh - 4px)" }}>
              {/* Close */}
              <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 24 }}>
                <button className="btn-close" onClick={closeModal} aria-label="Close" />
              </div>

              {modalLoading && (
                <div>
                  <div style={{ display: "flex", gap: 20, alignItems: "center", marginBottom: 24 }}>
                    <SkeletonBlock width={80} height={80} radius={8} style={{ flexShrink: 0 }} />
                    <div style={{ flex: 1 }}>
                      <SkeletonBlock height={28} style={{ marginBottom: 10 }} />
                      <SkeletonBlock width="55%" height={28} />
                    </div>
                  </div>
                  <SkeletonBlock height={14} style={{ marginBottom: 8 }} />
                  <SkeletonBlock height={14} style={{ marginBottom: 8 }} />
                  <SkeletonBlock width="70%" height={14} />
                </div>
              )}

              {!modalLoading && ipDetails && (
                <>
                  {/* Logo + title */}
                  <div style={{ display: "flex", gap: 20, alignItems: "center", marginBottom: 24 }}>
                    {ipDetails.attributes?.logo?.data?.[0] && (
                      <img
                        src={ipDetails.attributes.logo.data[0].attributes.url}
                        alt={ipDetails.attributes?.title}
                        loading="lazy"
                        decoding="async"
                        style={{ width: 80, height: 80, objectFit: "contain", flexShrink: 0 }}
                      />
                    )}
                    <h2 className="fw-bold mb-0" style={{ color: "#0a1a3a" }}>
                      {ipDetails.attributes?.title}
                    </h2>
                  </div>

                  <p style={{ color: "#4B5563", lineHeight: 1.75, marginBottom: 24 }}>
                    {ipDetails.attributes?.description}
                  </p>

                  {/* Actions row */}
                  <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginBottom: 28 }}>
                    {ipDetails.attributes?.attachment?.data?.attributes?.url && (
                      <a
                        href={ipDetails.attributes.attachment.data.attributes.url}
                        target="_blank"
                        rel="noreferrer"
                        className="btn btn-sm btn-outline-dark"
                      >
                        <i className="bi bi-download me-1" />
                        Download data sheet
                      </a>
                    )}
                    {ipDetails.attributes?.website && (
                      <a
                        href={ipDetails.attributes.website}
                        target="_blank"
                        rel="noreferrer"
                        className="btn btn-sm btn-dark"
                      >
                        Visit website →
                      </a>
                    )}
                  </div>

                  {/* Image gallery */}
                  {ipDetails.attributes?.gallery?.data?.length > 0 && (
                    <div style={{
                      display: "grid",
                      gridTemplateColumns: "repeat(auto-fill, minmax(180px, 1fr))",
                      gap: 12,
                    }}>
                      {ipDetails.attributes.gallery.data.map(img => (
                        <div key={img.id} style={{ borderRadius: 8, overflow: "hidden", border: "1px solid #e5e7eb" }}>
                          <img
                            src={img.attributes.url}
                            alt={img.attributes.alternativeText || "Product image"}
                            loading="lazy"
                            decoding="async"
                            style={{ width: "100%", height: 140, objectFit: "cover" }}
                          />
                        </div>
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default IpCarousel;
